import { PropsWithChildren } from 'react';
import { Navigate } from 'react-router';
import { useUserContext } from '../../hooks/userContext';
import { Pages, isAdminUser } from '../../utils';
import { CircularProgress, Box } from '@mui/material';
import { useDesignerOnboardingAccess } from '../../hooks/useDesignerOnboardingAccess';

type OnboardingStep = 'matching-profile' | 'bio' | 'designer-files';

interface DesignerOnboardingGuardProps extends PropsWithChildren {
  requiresStep: OnboardingStep;
  redirectTo?: string;
}

/**
 * DesignerOnboardingGuard enforces progressive designer onboarding.
 * Blocks access to features until designer completes the required onboarding step.
 */
export const DesignerOnboardingGuard: React.FC<DesignerOnboardingGuardProps> = ({ 
  children, 
  requiresStep,
  redirectTo = Pages.DASHBOARD 
}) => {
  const { user, loading: userLoading } = useUserContext();
  const { 
    isMatchingProfileUnlocked, 
    isBioUnlocked, 
    isDesignerFilesUnlocked,
    onboardingStatus,
    loading: onboardingLoading 
  } = useDesignerOnboardingAccess();

  const isLoading = userLoading || onboardingLoading;


  // Show loading while checking user status OR onboarding status
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // If no user, redirect to login
  if (!user) {
    return <Navigate to={Pages.LOGIN} replace />;
  }

  // Admins bypass onboarding checks
  if (isAdminUser(user)) {
    return <>{children}</>;
  }

  // Only apply to designers
  if (user.accountType !== 'Designer') {
    return <>{children}</>;
  }

  // Check if user has access to the required step
  let hasAccess = false;
  
  switch (requiresStep) {
    case 'matching-profile':
      hasAccess = isMatchingProfileUnlocked;
      break;
    case 'bio':
      hasAccess = isBioUnlocked;
      break;
    case 'designer-files':
      hasAccess = isDesignerFilesUnlocked;
      break;
  }


  // Check if user has reached required level
  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};